import React, { useState, useRef } from 'react';
import Sidebar from '../Sidebar';
import { apiPost } from '../../utils/apiConfig';
import 'bootstrap/dist/css/bootstrap.min.css';

function UserRegisterPage() {
  const [nombres, setNombres] = useState('');
  const [correo, setCorreo] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('CC');
  const [numeroDocumento, setNumeroDocumento] = useState('');
  const [rol, setRol] = useState('administrativo');
  const [mensaje, setMensaje] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastTipo, setToastTipo] = useState('success');
  
  // Estados para permisos granulares
  const [permisos, setPermisos] = useState({
    verDocumentosMedicos: false,
    verDocumentosAdministrativos: false,
    verReportes: false,
    enviarDocumentosMedicos: false,
    enviarDocumentosAdministrativos: false
  });

  // Estados para firma
  const [firmaPreview, setFirmaPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Mostrar toast con tipo específico
  const mostrarToast = (mensaje, tipo = 'success', duracion = 3000) => {
    setMensaje(mensaje);
    setToastTipo(tipo);
    setShowToast(true);
    
    if (tipo !== 'loading') {
      setTimeout(() => {
        setShowToast(false);
      }, duracion);
    }
  };

  // Ocultar toast manualmente
  const ocultarToast = () => {
    setShowToast(false);
  };

  // Función para asignar permisos automáticos según el rol
  const asignarPermisosPorRol = (rolSeleccionado) => {
    let nuevosPermisos = {};
    
    switch(rolSeleccionado) {
      case 'superadmin':
        nuevosPermisos = {
          verDocumentosMedicos: true,
          verDocumentosAdministrativos: true,
          verReportes: true,
          enviarDocumentosMedicos: true,
          enviarDocumentosAdministrativos: true
        };
        break;
        
      case 'enfermeria':
        nuevosPermisos = {
          verDocumentosMedicos: true,
          verDocumentosAdministrativos: false,
          verReportes: false,
          enviarDocumentosMedicos: true,
          enviarDocumentosAdministrativos: false
        };
        break;
        
      case 'administrativo':
        nuevosPermisos = {
          verDocumentosMedicos: false,
          verDocumentosAdministrativos: true,
          verReportes: true,
          enviarDocumentosMedicos: false,
          enviarDocumentosAdministrativos: true
        };
        break;
        
      case 'farmacia':
        nuevosPermisos = {
          verDocumentosMedicos: true,
          verDocumentosAdministrativos: false,
          verReportes: false,
          enviarDocumentosMedicos: true,
          enviarDocumentosAdministrativos: false
        };
        break;
        
      default:
        nuevosPermisos = {
          verDocumentosMedicos: false,
          verDocumentosAdministrativos: false,
          verReportes: false,
          enviarDocumentosMedicos: false,
          enviarDocumentosAdministrativos: false
        };
    }
    
    setPermisos(nuevosPermisos);
  };

  // Manejar cambio de rol
  const handleRolChange = (e) => {
    const nuevoRol = e.target.value;
    setRol(nuevoRol);
    asignarPermisosPorRol(nuevoRol);
  };

  // Manejar cambio individual de permisos
  const handlePermisoChange = (permiso) => {
    setPermisos(prev => ({
      ...prev,
      [permiso]: !prev[permiso]
    }));
  };

  // Manejar selección de archivo de firma
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      mostrarToast('Por favor selecciona un archivo de imagen', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      mostrarToast('La imagen no debe superar 2MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFirmaPreview(reader.result);
      mostrarToast('Firma cargada correctamente', 'success', 2000);
    };
    reader.readAsDataURL(file);
  };

  // Eliminar firma seleccionada
  const handleEliminarFirma = () => {
    setFirmaPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    mostrarToast('Firma eliminada', 'success', 2000);
  };

  // FUNCIÓN DE REGISTRO
  const handleRegister = async (e) => {
    e.preventDefault();

    // Validar dominio de correo
    const dominioPermitido = '@colombianadetrasplantes.com';
    if (!correo.endsWith(dominioPermitido)) {
      mostrarToast(`Solo se permiten correos del dominio ${dominioPermitido}`, 'error');
      return;
    }

    // Validar número de documento
    if (!numeroDocumento.trim()) {
      mostrarToast('El número de documento es obligatorio', 'error');
      return;
    }

    if (numeroDocumento.length < 5 || numeroDocumento.length > 15) {
      mostrarToast('El número de documento debe tener entre 5 y 15 caracteres', 'error');
      return;
    }

    mostrarToast('Creando usuario...', 'loading');

    try {
      const userData = { 
        nombres, 
        correo,
        tipoDocumento,
        numeroDocumento: numeroDocumento.trim(),
        rol,
        permisos
      };

      if (firmaPreview) {
        userData.firma = firmaPreview;
      }

      const data = await apiPost('/api/register', userData);

      mostrarToast(`Usuario ${nombres} creado exitosamente`, 'success', 4000);
      
      // Limpiar formulario
      setNombres('');
      setCorreo('');
      setTipoDocumento('CC');
      setNumeroDocumento('');
      setRol('administrativo');
      setPermisos({
        verDocumentosMedicos: false,
        verDocumentosAdministrativos: false,
        verReportes: false,
        enviarDocumentosMedicos: false,
        enviarDocumentosAdministrativos: false
      });
      setFirmaPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Error al registrar usuario:', error);
      const errorMsg = error.response?.data?.message || 'Error al crear usuario. Verifica los datos.';
      mostrarToast(errorMsg, 'error');
    }
  };

  // Inicializar permisos al cargar componente
  React.useEffect(() => {
    asignarPermisosPorRol(rol);
  }, []);

  // Componente Toggle Switch
  const ToggleSwitch = ({ id, checked, onChange, label, description }) => (
    <div className="permission-item mb-3">
      <div className="d-flex justify-content-between align-items-center">
        <div className="flex-grow-1">
          <label htmlFor={id} className="form-label mb-1 fw-bold">
            {label}
          </label>
          <small className="d-block text-muted">{description}</small>
        </div>
        <div className="ms-3">
          <div className="toggle-switch">
            <input
              type="checkbox"
              id={id}
              checked={checked}
              onChange={() => onChange()}
              className="toggle-input"
            />
            <label htmlFor={id} className="toggle-label">
              <span className="toggle-button"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style jsx>{`
        .toggle-switch {
          position: relative;
          display: inline-block;
        }

        .toggle-input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-label {
          display: block;
          width: 50px;
          height: 24px;
          background: #ccc;
          border-radius: 34px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .toggle-input:checked + .toggle-label {
          background: #28a745;
          box-shadow: 0 0 10px rgba(40, 167, 69, 0.3);
        }

        .toggle-button {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 50%;
          transition: all 0.3s ease;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .toggle-input:checked + .toggle-label .toggle-button {
          transform: translateX(24px);
        }

        .toggle-button::before {
          content: "✕";
          color: #dc3545;
          font-weight: bold;
          font-size: 12px;
          transition: all 0.3s ease;
        }

        .toggle-input:checked + .toggle-label .toggle-button::before {
          content: "✓";
          color: #28a745;
        }

        .permission-item {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 15px;
          transition: all 0.3s ease;
        }

        .permission-item:hover {
          background: #f1f3f4;
          border-color: #dee2e6;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .section-header {
          background: linear-gradient(135deg, #1a1e4b 0%, #2c3e50 100%);
          color: white;
          padding: 15px 20px;
          border-radius: 8px;
          margin-bottom: 20px;
          text-align: center;
        }

        .info-card {
          background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
          border: 1px solid #bbdefb;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 20px;
        }

        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 350px;
        }

        .toast-notification {
          background: white;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          padding: 20px;
          margin-bottom: 10px;
          border-left: 5px solid;
          animation: slideInRight 0.3s ease-out;
          position: relative;
        }

        .toast-notification.success {
          border-left-color: #28a745;
        }

        .toast-notification.error {
          border-left-color: #dc3545;
        }

        .toast-notification.loading {
          border-left-color: #007bff;
        }

        .toast-header {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          font-weight: bold;
        }

        .toast-icon {
          font-size: 20px;
          margin-right: 10px;
        }

        .toast-icon.success {
          color: #28a745;
        }

        .toast-icon.error {
          color: #dc3545;
        }

        .toast-icon.loading {
          color: #007bff;
          animation: spin 1s linear infinite;
        }

        .toast-close {
          position: absolute;
          top: 10px;
          right: 15px;
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: #999;
          transition: color 0.2s;
        }

        .toast-close:hover {
          color: #666;
        }

        .toast-message {
          color: #333;
          line-height: 1.4;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div className="d-flex">
        <Sidebar />

        <div className="flex-grow-1 p-4" style={{ marginLeft: '220px', transition: 'margin-left 0.3s' }}>
          <div className="container">
            <h1 className="text-center mb-4">Registrar Usuario</h1>

            <form onSubmit={handleRegister} className="bg-white p-4 rounded shadow-sm">

              {/* Información básica */}
              <div className="section-header">
                <h5 className="mb-0">Información Básica</h5>
              </div>
              
              <div className="row mb-4">
                {/* Nombre */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Nombres completos:</label>
                  <input
                    type="text"
                    value={nombres}
                    onChange={(e) => setNombres(e.target.value)}
                    required
                    className="form-control"
                    placeholder="Ej: Juan Pérez González"
                  />
                </div>

                {/* Correo con validación de dominio */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Correo electrónico:</label>
                  <input
                    type="email"
                    value={correo}
                    onChange={(e) => setCorreo(e.target.value)}
                    required
                    className="form-control"
                    placeholder="Ej: usuario@colombianadetrasplantes.com"
                  />
                  <small className="text-muted">
                    Solo se permiten correos del dominio @colombianadetrasplantes.com
                  </small>
                </div>

                {/* Tipo de Documento */}
                <div className="col-md-3 mb-3">
                  <label className="form-label fw-bold">Tipo de Documento:</label>
                  <select
                    value={tipoDocumento}
                    onChange={(e) => setTipoDocumento(e.target.value)}
                    required
                    className="form-select"
                  >
                    <option value="CC">Cédula de Ciudadanía</option>
                    <option value="TI">Tarjeta de Identidad</option>
                    <option value="CE">Cédula de Extranjería</option>
                    <option value="Pasaporte">Pasaporte</option>
                    <option value="PEP">Permiso Especial de Permanencia</option>
                  </select>
                </div>

                {/* Número de Documento */}
                <div className="col-md-3 mb-3">
                  <label className="form-label fw-bold">Número de Documento:</label>
                  <input
                    type="text"
                    value={numeroDocumento}
                    onChange={(e) => setNumeroDocumento(e.target.value.replace(/[^0-9]/g, ''))}
                    required
                    className="form-control"
                    placeholder="Ej: 1234567890"
                    maxLength="15"
                  />
                  <small className="text-muted">Solo números</small>
                </div>

                {/* Rol */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Rol:</label>
                  <select
                    value={rol}
                    onChange={handleRolChange}
                    required
                    className="form-select"
                  >
                    <option value="administrativo">Administrativo</option>
                    <option value="enfermeria">Enfermería</option>
                    <option value="farmacia">Farmacia</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </div>
              </div>

              {/* Permisos con toggles */}
              <div className="section-header">
                <h5 className="mb-0">Permisos del Usuario</h5>
              </div>

              <div className="info-card">
                <p className="mb-0 text-center">
                  <strong>Los permisos se asignan automáticamente según el rol, pero puedes personalizarlos:</strong>
                </p>
              </div>

              <div className="row">
                {/* Permisos de Visualización */}
                <div className="col-md-6">
                  <h4 className="text-primary mb-3 text-center fw-bold">Permisos de Visualización</h4>
                  
                  <ToggleSwitch
                    id="verDocumentosMedicos"
                    checked={permisos.verDocumentosMedicos}
                    onChange={() => handlePermisoChange('verDocumentosMedicos')}
                    label="Ver Documentos Médicos"
                    description="Consentimientos, autorizaciones médicas"
                  />

                  <ToggleSwitch
                    id="verDocumentosAdministrativos"
                    checked={permisos.verDocumentosAdministrativos}
                    onChange={() => handlePermisoChange('verDocumentosAdministrativos')}
                    label="Ver Documentos Administrativos"
                    description="Contratos, información sensible"
                  />

                  <ToggleSwitch
                    id="verReportes"
                    checked={permisos.verReportes}
                    onChange={() => handlePermisoChange('verReportes')}
                    label="Ver Reportes"
                    description="Estadísticas y métricas del sistema"
                  />
                </div>

                {/* Permisos de Acción */}
                <div className="col-md-6">
                  <h4 className="text-success mb-3 text-center fw-bold">Permisos de Acción</h4>
                  
                  <ToggleSwitch
                    id="enviarDocumentosMedicos"
                    checked={permisos.enviarDocumentosMedicos}
                    onChange={() => handlePermisoChange('enviarDocumentosMedicos')}
                    label="Enviar Documentos Médicos"
                    description="Crear y enviar documentos médicos"
                  />

                  <ToggleSwitch
                    id="enviarDocumentosAdministrativos"
                    checked={permisos.enviarDocumentosAdministrativos}
                    onChange={() => handlePermisoChange('enviarDocumentosAdministrativos')}
                    label="Enviar Documentos Administrativos"
                    description="Crear y enviar documentos administrativos"
                  />
                </div>
              </div>

              {/* FIRMA COMPACTA AL FINAL */}
              <div className="row mt-4">
                <div className="col-12">
                  <div className="card border-0" style={{background: '#f8f9fa'}}>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <h6 className="mb-1 fw-bold">✍️ Firma del Usuario (Opcional)</h6>
                          <small className="text-muted">
                            Puedes agregarla después desde la gestión de usuarios
                          </small>
                        </div>
                        {firmaPreview && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={handleEliminarFirma}
                            title="Eliminar firma"
                          >
                            🗑️ Eliminar
                          </button>
                        )}
                      </div>

                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <input
                            ref={fileInputRef}
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                          <small className="text-muted">JPG, PNG, GIF (máx. 2MB)</small>
                        </div>
                        <div className="col-md-4 text-center mt-3 mt-md-0">
                          {firmaPreview ? (
                            <img 
                              src={firmaPreview} 
                              alt="Firma" 
                              style={{
                                maxWidth: '100%',
                                maxHeight: '80px',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                padding: '5px',
                                background: 'white'
                              }}
                            />
                          ) : (
                            <div 
                              style={{
                                padding: '20px',
                                border: '1px dashed #dee2e6',
                                borderRadius: '4px',
                                background: 'white'
                              }}
                            >
                              <small className="text-muted">Sin firma</small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botón envío */}
              <div className="mt-4">
                <button
                  type="submit"
                  className="btn w-100 fw-bold py-3"
                  style={{ 
                    background: 'linear-gradient(135deg, #1a1e4b 0%, #2c3e50 100%)', 
                    color: 'white',
                    border: 'none',
                    fontSize: '16px'
                  }}
                >
                  Registrar Usuario
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* TOAST NOTIFICATION */}
      {showToast && (
        <div className="toast-container">
          <div className={`toast-notification ${toastTipo}`}>
            <button className="toast-close" onClick={ocultarToast}>×</button>
            <div className="toast-header">
              <div className={`toast-icon ${toastTipo}`}>
                {toastTipo === 'success' && '✓'}
                {toastTipo === 'error' && '✕'}
                {toastTipo === 'loading' && '⟳'}
              </div>
              <span>
                {toastTipo === 'success' && 'Éxito'}
                {toastTipo === 'error' && 'Error'}
                {toastTipo === 'loading' && 'Procesando'}
              </span>
            </div>
            <div className="toast-message">{mensaje}</div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserRegisterPage;